import { PenLine, ShieldAlert } from "lucide-react";
import SubmissionSuccessModal from "./SubmissionSuccessModal";
import { useState } from "react";

export default function ConfirmDetails({
  experiences = [],
  passportData = [],
  travelHistoryList = [],
  activeTab,
  onBack,
  onEditWork,
  onEditPassport,
  onEditTravel,
}) {
  const workFields = [
    {
      label: "Name of Last Organization",
      value: (exp) => exp.name_of_organization,
    },
    {
      label: "Working From",
      value: (exp) =>
        exp.working_from
          ? new Date(exp.working_from).toLocaleDateString("en-GB")
          : "-",
    },
    {
      label: "Job Industry",
      value: (exp) => exp.job_industry, // replace with lookup if needed
    },
    {
      label: "Job Role",
      value: (exp) => exp.job_role,
    },
    {
      label: "Job Type",
      value: (exp) => (exp.job_type === 1 ? "Full Time" : "Other"),
    },
    {
      label: "Income (As per ITR)",
      value: (exp) => exp.income,
    },
    {
      label: "Working Till",
      condition: (exp) => exp.currently_working === 0,
      value: (exp) =>
        exp.working_to
          ? new Date(exp.working_to).toLocaleDateString("en-GB")
          : "-",
    },
  ];
  const travelFields = [
    {
      label: "Travel Status",
      value: (t) => (t.travel_status === "1" ? "Visited" : "Not Visited"),
    },
    {
      label: "Choose Country",
      value: (t) => t.travel_country_name || "—",
    },
    {
      label: "Type of visa",
      value: (t) => t.types_of_visa_name || "—",
    },
    {
      label: "Visa status",
      value: (t) => t.visa_status || "—",
    },
    {
      label: "Date of Issue",
      value: (t) =>
        t.date_of_issue
          ? new Date(t.date_of_issue).toLocaleDateString("en-GB")
          : "—",
    },
  ];
  const passportFields = [
    {
      label: "Passport Number",
      value: (p) => p.passport_number || "—",
    },
    {
      label: "Citizens of",
      value: (p) => p.citizenship_country_name || "—",
    },
    {
      label: "Country of Issue",
      value: (p) => p.country_of_issue_name || "—",
    },
    {
      label: "Place of Issue",
      value: (p) => p.place_of_issue || "—",
    },
    {
      label: "Date of Issue",
      value: (p) =>
        p.date_of_issue
          ? new Date(p.date_of_issue).toLocaleDateString("en-GB")
          : "—",
    },
    {
      label: "Expiry Date",
      value: (p) =>
        p.expiry_date
          ? new Date(p.expiry_date).toLocaleDateString("en-GB")
          : "—",
    },
    {
      label: "Country of Residence",
      value: (p) => p.country_of_residence_name || "—",
    },
  ];
  const [openSuccess, setOpenSuccess] = useState(false);
  const [success, setSuccess] = useState(true);

  return (
    <div className="space-y-6 bg-[#FFFDF6]">
      {openSuccess && (
        <SubmissionSuccessModal
          onClose={() => setOpenSuccess(false)}
          success={success}
        />
      )}
      <div className="text-md font-inter text-black flex items-center gap-2">
        <span className="text-orange-500">
          <ShieldAlert />
        </span>
        Please review your details before confirmation
      </div>

      {/* WORK */}
      {activeTab === "work" && (
        <div className="space-y-6">
          {experiences.length === 0 ? (
            <p className="text-gray-500">No work history added</p>
          ) : (
            experiences.map((exp, index) => (
              <div
                key={exp.id}
                className="border border-gray-200 rounded-2xl bg-white"
              >
                {/* HEADER */}
                <div className="flex items-center justify-between px-5 py-3 border-b">
                  <div className="flex items-center gap-2">
                    {/* Icon */}
                    <div className="">
                      <img src="/bag.png" alt="bag" />
                    </div>
                    <p className="font-inter font-bold text-base leading-none tracking-normal text-[#572575]">
                      Experience {index + 1}
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    {exp.currently_working === 1 && (
                      <span className="text-green-600 border border-green-600 px-3 py-1 rounded-md text-sm font-inter font-semibold bg-green-50 ">
                        Currently Working
                      </span>
                    )}

                    <button
                      onClick={() => onEditWork(index)}
                      className="text-blue-600 text-sm font-inter font-semibold flex items-center gap-1"
                    >
                      <PenLine size={14} /> Edit
                    </button>
                  </div>
                </div>

                {/* BODY */}
                <div className="grid grid-cols-2 gap-y-4 gap-x-10 px-5 py-4 text-sm">
                  {workFields.map((field, idx) => {
                    if (field.condition && !field.condition(exp)) return null;

                    return (
                      <div key={idx}>
                        <p className="text-[#555555] font-inter">
                          {field.label}
                        </p>
                        <p className="text-[#111111] font-semibold font-inter">
                          {field.value(exp)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* PASSPORT */}
      {activeTab === "passport" && (
        <div className="space-y-6">
          {passportData?.length === 0 ? (
            <p className="text-gray-500">No passport details added</p>
          ) : (
            passportData.map((p) => (
              <div
                key={p.id}
                className="border border-gray-200 rounded-2xl bg-white"
              >
                {/* HEADER */}
                <div className="flex items-center justify-between px-5 py-3 border-b">
                  <div className="flex items-center gap-2">
                    <img
                      src="/passport.png"
                      alt="passport"
                      className="h-8 w-8"
                    />
                    <p className="font-inter font-bold text-base text-[#572575]">
                      Passport Details
                    </p>
                  </div>

                  <button
                    onClick={() => onEditPassport(p)}
                    className="text-blue-600 text-sm font-inter font-semibold flex items-center gap-1"
                  >
                    <PenLine size={14} /> Edit
                  </button>
                </div>

                {/* FILE BOX */}
                <div className="px-5 pt-4">
                  <div className="flex items-center justify-between border rounded-xl px-4 py-3 bg-white shadow-sm">
                    <div className="flex items-center gap-3">
                      <img src="/pdf.png" alt="pdf" className="w-8 h-8" />
                      <div>
                        <p className="font-inter font-medium text-sm">
                          Passport scan.pdf
                        </p>
                        <p className="text-xs text-gray-500">Size: 2.4 MB</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 text-green-600 font-inter font-medium text-sm">
                      ✓ Verified
                    </div>
                  </div>
                </div>

                {/* BODY */}
                <div className="grid grid-cols-2 gap-y-4 gap-x-10 px-5 py-5 text-sm">
                  {passportFields.map((field, idx) => (
                    <div key={idx}>
                      <p className="text-[#555555] font-inter">{field.label}</p>
                      <p className="text-[#111111] font-semibold font-inter">
                        {field.value(p)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* TRAVEL */}
      {activeTab === "travel" && (
        <div className="space-y-6">
          {travelHistoryList.length === 0 ? (
            <p className="text-gray-500">No travel history added</p>
          ) : (
            travelHistoryList.map((t, index) => (
              <div
                key={t.id}
                className="border border-gray-200 rounded-2xl bg-white"
              >
                {/* HEADER */}
                <div className="flex items-center justify-between px-5 py-3 border-b">
                  <div className="flex items-center gap-2">
                    <div>
                      <img src="/globe.png" alt="travel" />
                    </div>
                    <p className="font-inter font-bold text-base text-[#572575]">
                      Travel History
                    </p>
                  </div>

                  <button
                    onClick={() => onEditTravel(index)}
                    className="text-blue-600 text-sm font-inter font-semibold flex items-center gap-1"
                  >
                    <PenLine size={14} /> Edit
                  </button>
                </div>

                {/* FILE BOX */}
                <div className="px-5 pt-4">
                  <div className="flex items-center justify-between border rounded-xl px-4 py-3 bg-white shadow-sm">
                    <div className="flex items-center gap-3">
                      <img src="/pdf.png" alt="pdf" className="w-8 h-8" />
                      <div>
                        <p className="font-inter font-medium text-sm">
                          Visa Details scan.pdf
                        </p>
                        <p className="text-xs text-gray-500">Size: 2.4 MB</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 text-green-600 font-inter font-medium text-sm">
                      ✓ Verified
                    </div>
                  </div>
                </div>

                {/* BODY */}
                <div className="grid grid-cols-2 gap-y-4 gap-x-10 px-5 py-5 text-sm">
                  {travelFields.map((field, idx) => (
                    <div key={idx}>
                      <p className="text-[#555555] font-inter">{field.label}</p>
                      <p className="text-[#111111] font-semibold font-inter">
                        {field.value(t)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="text-sm text-[#572575] underline">
          ← Back to edit
        </button>

        <button
          onClick={() => setOpenSuccess(true)}
          className="bg-[#572575] text-white px-6 py-2 rounded-lg"
        >
          Confirm & Continue
        </button>
      </div>
    </div>
  );
}
